import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { IUserModel } from '@packages/global';
import { useTranslation } from 'react-i18next';

interface ModelSelectorProps {
  models: IUserModel[];
  value: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

export default function ModelSelector({
  models,
  value,
  onChange,
  disabled = false,
  loading = false
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedModel = models.find(m => m.id === value);
  const { t } = useTranslation('editor');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (modelId: string) => {
    onChange(modelId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled || loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <CpuChipIcon className="h-4 w-4 text-green-400" />
        <span className="max-w-[80px] truncate">
          {loading ? t('common.loading') : (selectedModel?.title || t('common.selectModel'))}
        </span>
        <ChevronDownIcon className="h-3 w-3 text-gray-500" />
      </button>

      {isOpen && models.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden z-20 max-h-48 overflow-y-auto min-w-[180px]">
          {models.map(model => (
            <button
              key={model.id}
              onClick={() => handleSelect(model.id)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-700 transition-colors ${value === model.id ? 'text-blue-400 bg-gray-700/50' : 'text-gray-300'
                }`}
            >
              {model.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}